/**
 * SetFilterDropdown - Set filter built on the generic FilterDropdown.
 *
 * Loads sets from Firestore via useSetsQuery, maps them into FilterDropdown
 * options grouped by set_type, with keyrune/Scryfall set icons.
 */
import type { ReactElement } from 'react';
import { useMemo } from 'react';

import type { FilterDropdownOption, FilterDropdownOptionGroup } from '@/components/FilterDropdown/FilterDropdown';
import FilterDropdown from '@/components/FilterDropdown/FilterDropdown';
import SetIcon from '@/components/SetIcon/SetIcon';
import { transformSetsToOptions, useSetsQuery } from '@/features/sets/hooks/api';

// ============================================================================
// Types
// ============================================================================

export interface SetFilterDropdownProps {
  value: string[] | undefined;
  onChange: (setCodes: string[] | undefined) => void;
}

// ============================================================================
// Trigger icon
// ============================================================================

const TRIGGER_ICON = <i className="ss ss-pmtg1 ss-fw" style={{ fontSize: '14px' }} aria-hidden="true" />;

// ============================================================================
// Component
// ============================================================================

export default function SetFilterDropdown({ value, onChange }: SetFilterDropdownProps): ReactElement {
  const { data: sets } = useSetsQuery();

  const { groups, options } = useMemo(() => {
    if (!sets) return { groups: [] as FilterDropdownOptionGroup[], options: [] as FilterDropdownOption[] };

    const reactSelectGroups = transformSetsToOptions(sets);

    const dropdownGroups: FilterDropdownOptionGroup[] = reactSelectGroups.map((g) => ({
      groupName: g.label,
    }));

    const dropdownOptions: FilterDropdownOption[] = reactSelectGroups.flatMap((g) =>
      g.options.map((opt) => ({
        value: opt.value,
        label: opt.set.name,
        icon: <SetIcon setCode={opt.set.code} scryfallIconUri={opt.set.icon_svg_uri} size="sm" className="shrink-0" />,
        group: g.label,
      })),
    );

    return { groups: dropdownGroups, options: dropdownOptions };
  }, [sets]);

  const selectedValues = value ?? [];

  const handleToggle = (setCode: string) => {
    const current = selectedValues;
    const newValues = current.includes(setCode) ? current.filter((v) => v !== setCode) : [...current, setCode];
    onChange(newValues.length > 0 ? newValues : undefined);
  };

  const handleClear = () => {
    onChange(undefined);
  };

  return (
    <FilterDropdown
      triggerLabel="Sets"
      triggerIcon={TRIGGER_ICON}
      ariaLabel="Set filter"
      searchPlaceholder="Search sets..."
      groups={groups}
      options={options}
      selectedValues={selectedValues}
      onToggle={handleToggle}
      onClear={handleClear}
    />
  );
}
